import type { UIStrings, SEOMetadata } from './base'

export const enStrings: UIStrings = {
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous'
  },

  nav: {
    home: 'Home',
    about: 'About',
    guides: 'Optimization Guides',
    languageSwitch: 'Language',
    selectLanguage: 'Select Language'
  },

  header: {
    title: 'SrcRank',
    subtitle: 'SEO / AEO / GEO Tool',
    description:
      'Check whether your content is easier to understand, extract, and cite across search and generative AI systems.'
  },

  hero: {
    intro:
      'This tool is built by Taiwan SEO strategist <strong>Yu-Ting Chiu (Black Teacher)</strong> after years of hands-on research. It combines Google\'s Search Quality Rater Guidelines, the Helpful Content Update (HCU), and global SEO evaluation standards to assess whether your content is easier for search systems to understand, easier for generative AI to extract, and more likely to be cited by AI systems. If you want a more systematic AEO / GEO foundation, you can also join Black Teacher\'s <a href="https://aeogeo.thinkwithblack.com/" target="_blank" rel="sponsored noopener noreferrer">Content Structure</a> course to build site content that is structurally stronger for AI citation.',
    disclaimer:
      'Disclaimer: This tool provides third-party diagnostics and optimization suggestions. Search rankings and traffic growth are not guaranteed.'
  },

  dashboard: {
    totalScore: 'Total Score',
    structureScore: 'Structure Score',
    strategyScore: 'Strategy Score',
    why: 'WHY - Problem Definition',
    how: 'HOW - Implementation Method',
    what: 'WHAT - Solution',
    guide: 'Guide',
    viewGuide: 'View Optimization Guide',
    noGuideAvailable: 'No guide available',
    failedToLoadGuide: 'Failed to load optimization guide'
  },

  guides: {
    title: 'Optimization Guides',
    description: 'Detailed content optimization recommendations',
    optimization: 'Optimization Guide',
    reasons: 'Root Causes',
    diagnosis: 'Diagnostic Methods',
    improvements: 'Improvement Strategies',
    actions: 'Action Plan',
    faq: 'Frequently Asked Questions',
    quickReference: 'Quick Reference'
  },

  analysis: {
    submitEmail: 'Submit Email',
    emailPlaceholder: 'Enter your email address',
    submit: 'Submit',
    submitting: 'Submitting...',
    checkResults: 'Check Results',
    resultsWillBeSent: 'Analysis results will be sent to your email',
    enterValidEmail: 'Please enter a valid email address',
    asyncTitle: 'Long-running job email mode',
    asyncDescription: 'This is not the default path. Use it only when you intentionally want background processing and full delivery by email.',
    queuedTitle: 'Analysis Submitted',
    queuedLine1: 'This run has been moved to background processing so you do not need to wait on the page.',
    queuedLine2: 'When it finishes, we will email the full result to:',
    taskIdLabel: 'Task ID',
    durationHint: 'Analysis typically takes 1-5 minutes. If the email renders poorly, the same result remains available from the private results link.',
    helperTip: 'Tip: use this mode only for longer jobs or when you do not want to stay on the page.',
    submitFailed: 'Submission failed. Please try again.'
  },

  metrics: {
    intentFit: 'Search Intent Satisfaction',
    helpfulRatio: 'Helpful Ratio',
    depthCoverage: 'Content Depth & Coverage',
    intentExpansion: 'Intent Expansion & Keyword Coverage',
    actionability: 'Actionability',
    readabilityRhythm: 'Readability & Narrative Rhythm',
    structureHighlights: 'Structure Highlights',
    authorBrandSignals: 'Author & Brand Recognition',
    evidenceSupport: 'Evidence & Citations',
    experienceSignals: 'First-hand Experience & Case Studies',
    narrativeDensity: 'Narrative Specificity & Information Density',
    freshnessSignals: 'Freshness & Update Signals',
    expertPerspective: 'Expert Perspective & Judgment',
    extractability: 'Answer Extractability',
    keySummary: 'Key Summary & Highlights',
    conversationalGuidance: 'Conversational Tone & Guidance',
    readerActivation: 'Reader Engagement & Follow-up'
  },

  status: {
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    poor: 'Needs Improvement',
    veryPoor: 'Urgent Improvement Needed'
  },

  errors: {
    networkError: 'Network connection error',
    serverError: 'Server error',
    invalidInput: 'Invalid input',
    notFound: 'Not found',
    unauthorized: 'Unauthorized'
  },

  input: {
    contentLabel: 'Content',
    contentPlaceholder: 'Paste your article content here...',
    wordCountLabel: 'Word Count',
    wordCountUnit: 'characters',
    keywordsLabel: 'Target Keywords',
    keywordsHint: '(required, 1-5 keywords; press Enter or comma to add)',
    keywordsPlaceholder: 'e.g. ai tools',
    deliveryModeLabel: 'How to receive results',
    deliveryModeHint: 'Instant analysis stays the default. Switch only when this looks like a longer-running analysis and you want email delivery.',
    deliverySyncLabel: 'Instant analysis',
    deliveryAsyncLabel: 'Email me when finished',
    deliveryAsyncDescription: 'Best for longer content or jobs you do not want to wait on. We will process it in the background and send the full result by email.',
    emailLabel: 'Email Address',
    emailOptionalHint: '(used only for long-running background analysis)',
    emailRequiredHint: '(required for background delivery)',
    emailPlaceholder: 'your.email@example.com',
    submitSync: 'Start AI Analysis',
    submitAsync: 'Start background analysis and email the result',
    submitLoading: 'Analyzing...',
    errorEmptyContent: 'Please enter your article content.',
    errorKeywordsRequired: 'Please provide between 1 and 5 target keywords.',
    errorKeywordsMax: 'You can specify up to 5 target keywords only.',
    errorInvalidEmail: 'Please enter a valid email address.',
    analyzing: 'AI is analyzing your content...',
    analyzingHint: 'This may take a few seconds. Please wait.'
  },

  results: {
    whyTitle: 'Why - Problem Definition',
    whyDescription: 'Why should you care about this analysis?',
    howTitle: 'How - Implementation Method',
    howDescription: 'How is your score calculated?',
    whatTitle: 'What - Solution',
    whatDescription: 'What can you do to improve?',
    overallScoreTitle: 'v5 Overall Score',
    overallScoreDescription: 'Latest v5 scoring model with structure (40%) and strategy (60%) weighted overall performance.',
    noV5ScoreFallback: 'v5 score not yet available. Below is the legacy overall score.',
    structureScoreTitle: 'Structure Score',
    structureScoreDescription: 'Review whether your content has good structure, readability, evidence, and experience support.',
    strategyScoreTitle: 'Strategy Score',
    strategyScoreDescription: 'Evaluate whether your Why / How / What strategic framework is complete and resonates with your target audience.',
    priorityRecommendations: 'Priority Improvement Recommendations',
    priorityRecommendationsDescription: 'Based on v5 scoring of structure and strategy dimensions, the following recommendations should be prioritized to quickly boost your overall score.',
    structureInsights: 'Structure Insights',
    structureInsightsDescription: 'Focus on paragraph structure, summary organization, conversational tone, and reader engagement to make content more readable and understandable.',
    strategyInsights: 'Strategy Insights',
    strategyInsightsDescription: 'Cover helpfulness, content depth, credibility, and keyword coverage to align your content with search and target reader needs.',
    sourceTextReview: 'Source Text Review',
    sourceTextReviewDescription: 'Review source content paragraph by paragraph, paired with recommendations above to adjust text, examples, and structure.',
    paragraph: 'Paragraph',
    originalContent: 'Original Content',
    tokens: 'Tokens',
    segments: 'Segments',
    format: 'Format',
    collapse: 'Collapse',
    expand: 'Expand',
    keySignals: 'Key Signals',
    evidencePoints: 'Evidence Points',
    noMetricsAvailable: 'No metrics data available at this time.',
    insufficientMetadata: 'Unable to Score: Missing HTML Metadata',
    metadataUnavailable: 'Metadata Detectable',
    schemaUnavailable: 'Schema Detectable',
    undetectableItems: 'Undetectable Items',
    hint: 'Tip: Please paste the complete page HTML or use an API that provides source code so the system can access Meta / Schema / author information and other key tags.',
    detectionStatus: 'Current Detection Status',
    yes: 'Yes',
    no: 'No',
    loadGuideError: 'Unable to load optimization guide, please try again later.',
    loadGuideErrorRetry: 'An error occurred while loading the guide, please try again later.',
    notEvaluatedYet: 'Not Yet Evaluated',
    excellentPerformance: 'Excellent Performance',
    canBeImproved: 'Can Be Improved',
    priorityImprovement: 'Priority Improvement',
    urgentImprovement: 'Urgent Improvement',
    weight: 'Weight',
    highPriority: 'High Priority',
    mediumPriority: 'Medium Priority',
    lowPriority: 'Low Priority',
    suggestion: 'Suggestion',
    category: 'Category',
    lowScoreWarning: 'This metric is currently underperforming. We recommend prioritizing improvements and refer to the metrics and recommendations below for specific actions.',
    metricHelpfulRatio: 'Helpful Ratio',
    metricIntentFit: 'Search Intent Fit',
    metricDepthCoverage: 'Content Depth & Coverage',
    metricIntentExpansion: 'Intent Expansion & Keyword Coverage',
    metricActionability: 'Actionability',
    metricReadabilityRhythm: 'Readability & Narrative Rhythm',
    metricStructureHighlights: 'Structure Highlights',
    metricAuthorBrandSignals: 'Author & Brand Recognition',
    metricEvidenceSupport: 'Evidence & Citations',
    metricExperienceSignals: 'First-hand Experience & Case Studies',
    metricNarrativeDensity: 'Narrative Specificity & Information Density',
    metricFreshnessSignals: 'Freshness & Update Signals',
    metricExpertPerspective: 'Expert Perspective & Judgment',
    metricExtractability: 'Answer Extractability',
    metricKeySummary: 'Key Summary & Highlights',
    metricConversationalGuidance: 'Conversational Tone & Guidance',
    metricReaderActivation: 'Reader Engagement & Follow-up',
    metricHelpfulRatioDesc: 'Measures the proportion of helpful content vs. unhelpful or off-topic content based on HCU signals.',
    metricIntentFitDesc: 'Evaluates how well your content matches the search intent and provides answers in the first paragraph.',
    metricDepthCoverageDesc: 'Assesses content depth, topic coherence, and semantic focus to ensure comprehensive coverage.',
    metricIntentExpansionDesc: 'Checks if content addresses related questions and covers relevant keywords beyond the main topic.',
    metricActionabilityDesc: 'Measures how actionable and practical your content is with clear steps and guidance.',
    metricReadabilityRhythmDesc: 'Evaluates sentence structure, paragraph length, and narrative flow for optimal readability.',
    metricStructureHighlightsDesc: 'Assesses the use of lists, tables, and other structural elements to organize information.',
    metricAuthorBrandSignalsDesc: 'Checks for author information and brand recognition signals to establish credibility.',
    metricEvidenceSupportDesc: 'Evaluates the presence of citations, data, and external references to support claims.',
    metricExperienceSignalsDesc: 'Measures first-hand experience, case studies, and real-world examples in the content.',
    metricNarrativeDensityDesc: 'Assesses the specificity and information density of the narrative and examples.',
    metricFreshnessSignalsDesc: 'Checks for recent dates, updated information, and freshness indicators.',
    metricExpertPerspectiveDesc: 'Evaluates expert terminology, professional judgment, and comparative analysis.',
    metricExtractabilityDesc: 'Measures how easily AI systems can extract answers from individual paragraphs.',
    metricKeySummaryDesc: 'Checks for key takeaways, summaries, and highlighted information for quick scanning.',
    metricConversationalGuidanceDesc: 'Evaluates conversational tone and reader guidance cues throughout the content.',
    metricReaderActivationDesc: 'Measures engagement elements like CTAs and follow-up questions to encourage reader interaction.',
    categoryContent: 'Content',
    categoryTrust: 'Trust',
    categoryExperience: 'Reader Experience',
    categoryStructure: 'Structure',
    categoryStrategy: 'Strategy'
  },

  scoreCard: {
    scoreComposition: 'Score Composition',
    expandExplanation: 'Show Explanation',
    collapseExplanation: 'Hide Explanation',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    needsImprovement: 'Needs Improvement',
    headingStructure: 'Heading Structure',
    contentOrganization: 'Content Organization',
    readability: 'Readability',
    evidence: 'Evidence & Citations',
    experience: 'Practical Experience',
    freshness: 'Freshness & Updates',
    actionability: 'Actionability',
    semanticQuality: 'Semantic Quality',
    credibility: 'Credibility',
    trustSignals: 'Trust Signals',
    why: 'Why (Problem)',
    how: 'How (Method)',
    what: 'What (Solution)',
    overallScore: 'Overall Strategy Score',
    structure: 'Structure',
    strategy: 'Strategy',
    structureExplanationIntro: 'Content structure signals including paragraph layout, readability, evidence, and experience account for 40% of the v5 score, ensuring a solid article framework.',
    strategyExplanationIntro: 'The depth of the Why/How/What strategic framework accounts for 60% and reflects whether content truly addresses reader and search needs.',
    headingStructureExpl: 'Check if H1/H2 hierarchy is clear and titles focus on the topic, helping readers and search engines quickly grasp the outline.',
    contentOrganizationExpl: 'Evaluate the use of paragraphs, lists, tables and other structural elements to ensure information is organized and not scattered.',
    readabilityExpl: 'Measure sentence length and long paragraph ratio to ensure smooth language flow and appropriate paragraph length, reducing reading burden.',
    evidenceExpl: 'Review whether data, years, and reliable sources are cited to support claims and enhance credibility.',
    experienceExpl: 'Detect first-person experience, case studies, or practical insights to strengthen the "experience" signal in EEAT.',
    freshnessExpl: 'Confirm if update dates are disclosed and recent information is cited, avoiding outdated content affecting search credibility.',
    actionabilityExpl: 'Check if content provides steps, checklists, or concrete suggestions to help readers take immediate action.',
    semanticQualityExpl: 'For HTML mode, evaluate semantic naturalness and paragraph focus to avoid keyword stuffing.',
    contentLengthExpl: 'For plain text mode, evaluate word count and paragraph density to ensure comprehensive topic coverage and maintain pacing.',
    whyExpl: 'Explain why this topic matters and whether it specifically depicts reader pain points and context, establishing credibility.',
    howExpl: 'Evaluate whether content provides clear steps, methods, and execution guidelines so readers know what to do.',
    whatExpl: 'Check if final claims and calls-to-action are clear, helping readers understand next steps or value gained.',
    overallScoreExpl: 'The strategic performance averaged across the three Why/How/What dimensions, measuring overall persuasiveness and narrative completeness.'
  },

  recommendations: {
    title: 'Optimization Recommendations',
    noRecommendations: 'Great! There are currently no areas that need improvement.',
    example: 'Example',
    helpful: 'Helpful',
    notApplicable: 'Not Applicable',
    categoryContent: 'Content',
    categoryTrust: 'Trust',
    categoryExperience: 'Reader Experience',
    categoryStructure: 'Structure',
    categoryStrategy: 'Strategy'
  },

  v5Dashboard: {
    analyzing: 'Analysis in progress...',
    noResults: 'No Results Available',
    pleaseSubmit: 'Please submit content for analysis',
    overallScore: 'Overall Score',
    outOf: '/100',
    excellent: '🌟 Excellent - Outstanding content quality',
    good: '👍 Good - Solid foundation, room for optimization',
    fair: '⚠️ Fair - Needs improvement',
    needsImprovement: '❌ Needs Improvement - Consider revising content strategy',
    structureScore: 'Structure Score',
    strategyScore: 'Strategy Score',
    structureDetails: 'Structure Details',
    strategyDetails: 'Strategy Details (WHY/HOW/WHAT)',
    whyLabel: 'WHY - Problem Definition',
    howLabel: 'HOW - Implementation Method',
    whatLabel: 'WHAT - Solution',
    weight: 'Weight',
    suggestions: 'Improvement Suggestions',
    detectionStatus: 'Current Detection Status',
    metadataDetectable: 'Metadata Detectable',
    schemaDetectable: 'Schema Detectable',
    yes: 'Yes',
    no: 'No'
  },

  email: {
    subject: '✅ Your AI content analysis is ready',
    headerTitle: '✨ Analysis Complete',
    headerSubtitle: 'Your content has finished the AI strategy evaluation',
    scoreLabel: 'Overall Score / 100',
    structureWeight: 'Structure Score · 40% Weight',
    strategyWeight: 'Strategy Score · 60% Weight',
    recommendationsTitle: 'Priority Action Items',
    recommendationsDescription: 'Start with the items marked in red to maximize score impact.',
    recommendationsEmpty: 'Great news! There are no immediate action items required for now.',
    todoExampleLabel: 'Example',
    viewButton: 'View Full Analysis',
    viewButtonDescription: 'Tap the button to read the full breakdown, WHY / HOW / WHAT analysis, and recommendations.',
    taskIdLabel: 'Task ID',
    footerNotice: '© 2025 SrcRank | ',
    footerNoReply: 'This email was sent automatically. Please do not reply directly.',
    interpretation: {
      excellent: '🌟 Excellent - Outstanding content quality, strong AI citation potential',
      good: '👍 Good - Solid foundation, room for optimization',
      fair: '⚠️ Fair - Needs improvement, review recommendations',
      poor: '❌ Needs Improvement - Consider revisiting your content strategy'
    },
    priorityLabelHigh: 'High Priority',
    priorityLabelMedium: 'Medium Priority',
    priorityLabelLow: 'Low Priority',
    textTitle: 'SrcRank Analysis Result',
    textScoreHeading: 'Overall Score',
    textBreakdownHeading: 'Score Breakdown',
    textRecommendationsHeading: 'Priority Action Items',
    textViewHeading: 'View Full Analysis'
  },

  footer: {
    copy:
      '© 2025 SrcRank (Powered by <span>YuYan Consulting Co., Ltd. (TW)</span> &amp; <a href="https://toldyou.co" target="_blank" rel="noopener noreferrer" class="text-primary-300 hover:text-primary-200 underline">Tōgen Consulting Co., Ltd. (JP)</a>). All rights reserved.'
  },

  resultsPage: {
    missingTaskId: 'Missing Task ID',
    loadingResults: 'Loading Results...',
    queryingResults: 'Querying your analysis results from server',
    queryFailed: 'Query Failed',
    noResults: 'No Results Available',
    checkTaskId: 'Please check if the task ID is correct',
    backToHome: 'Back to Home',
    analysisResults: 'Analysis Results',
    taskId: 'Task ID',
    completedAt: 'Completed At',
    submittedContent: 'Submitted Content',
    characterCount: 'Characters',
    keywords: 'Keywords',
    none: 'None',
    strategyAnalysisDetails: 'Strategy Analysis Details',
    whyProblem: 'WHY - Problem Definition',
    howImplementation: 'HOW - Implementation Method',
    whatSolution: 'WHAT - Solution',
    evidence: 'Evidence',
    improvementSuggestions: 'Improvement Suggestions',
    notFound: 'Result not found. Please check if the task ID is correct or if the result has expired (7 days).',
    resultExpired: 'Query failed'
  },

  scoreHistory: {
    title: 'Score Tracking Dashboard',
    description: 'Keep the latest 200 analyses and establish periodic tracking and automatic export processes.',
    exportButton: 'Export CSV',
    clearButton: 'Clear History',
    noHistory: 'No history records yet. After completing an analysis, the system will automatically add it to the tracking dashboard.',
    averageOverallScore: 'Average Overall Score',
    latestTrend: 'Latest Trend',
    sevenDayAnalysis: '7-Day Analysis',
    nextSchedule: 'Next Schedule',
    notScheduled: 'Not Scheduled',
    notScheduledHint: 'The system uses the latest analysis date + 7 days as the routine review date.',
    trendComparison: 'Compared with the previous overall score',
    sevenDayHint: 'Recommended at least 3 times per week to keep content fresh.',
    nextReviewHint: 'The system uses the latest analysis date + 7 days as the routine review date.',
    latestRecords: 'Latest Analysis Records',
    timeHeader: 'Time',
    keywordsHeader: 'Keywords',
    overallHeader: 'Overall',
    aeoHeader: 'AEO',
    seoHeader: 'SEO',
    gapHeader: 'Gap',
    weakFlagsHeader: 'Weak Flags',
    noGaps: 'None',
    noWeakFlags: 'None',
    exportProcess: 'Export Process',
    maintenanceSuggestions: 'Maintenance Suggestions',
    exportStep1: 'After completing an analysis, the dashboard will automatically add the record.',
    exportStep2: 'Click "Export CSV" to download the latest 200 records.',
    exportStep3: 'Upload the CSV to your data warehouse or Google Sheet for long-term tracking.',
    maintenanceStep1: 'Analyze at least 3 times per week to keep content up-to-date.',
    maintenanceStep2: 'At the beginning of each month, organize the previous month\'s CSV export and update the KPI dashboard.',
    maintenanceStep3: 'When two consecutive scores fall below 60, create a high-priority correction ticket.',
    flat: 'Flat'
  }
}

export const enSEO: Record<string, SEOMetadata> = {
  home: {
    title: 'SEO/AEO/GEO Tool | SrcRank: Your AI Content Strategy Advisor',
    description: 'SrcRank is an SEO/AEO/GEO tool built by Black Teacher to help site owners evaluate whether content is easier for search systems to understand, easier for generative AI to extract, and more likely to become an AI-cited source.',
    keywords: ['SEO', 'AEO', 'GEO', 'content optimization', 'AI analysis', 'AI citation']
  },
  guides: {
    title: 'Optimization Guides - SrcRank',
    description: 'Detailed optimization guides for each scoring metric. Understand why your score is low and get actionable improvement strategies.'
  },
  analysis: {
    title: 'Async Content Analysis - SrcRank',
    description: 'Submit your content for asynchronous AI scoring. Receive structured SEO and AEO insights directly in your inbox once processing completes.',
    keywords: ['async analysis', 'AI content scoring', 'email results', 'content audit']
  },
  results: {
    title: 'Analysis Results Dashboard - SrcRank',
    description: 'Review your completed AI content analysis with detailed structure and strategy scoring, gap diagnostics, and prioritized improvement actions.',
    keywords: ['analysis results', 'content dashboard', 'SEO insights', 'AEO scoring']
  }
}
